import React, { useEffect, useState } from 'react';
import './Temporadas.css';
import { generarCalendario, previsualizarCalendario, borrarCalendario } from '../../services/jornadasService';
import { getModalidadesReal, actualizarIdaVuelta } from '../../services/temporadasService';

const MODALIDADES = [
  'DOBLES FEMENINO', 'DOBLES MASCULINO', 'DOBLES MIXTO',
  'INDIVIDUAL FEMENINO', 'INDIVIDUAL MASCULINO',
];

export default function RoundRobinTab({ temporada, jornadas, participantes, onRefresh, soloLectura }) {
  const [modalidades, setModalidades] = useState([]);
  const [generando, setGenerando] = useState(false);
  const [msg, setMsg] = useState(null);
  // Borradores calculados pero sin guardar: { [modalidad]: previsualizacionModalidad }
  const [borradores, setBorradores] = useState({});
  const [guardandoMod, setGuardandoMod] = useState(null);

  const cargarModalidades = () => getModalidadesReal(temporada).then(setModalidades);

  useEffect(() => { cargarModalidades(); }, [temporada]); // eslint-disable-line react-hooks/exhaustive-deps

  const nombrePorParticipante = Object.fromEntries(
    (participantes ?? []).map((p) => [p.participante_id, p.nombre_mostrar]),
  );

  const jornadasPorModalidad = (mod) => jornadas.filter(j => j.modalidad === mod);

  const mostrarResultado = (resultados, { esPrevisualizacion } = {}) => {
    const avisos = resultados.filter(r => r.forzadoSoloIda);
    const textoOk = resultados.map(r => `${r.modalidad}: ${r.partidos} partidos`).join(' · ');
    const sufijo = esPrevisualizacion ? ' (borrador, sin guardar — revisa y pulsa "Guardar" en cada modalidad).' : '';
    if (avisos.length) {
      setMsg({
        tipo: 'aviso',
        texto: `${textoOk}${sufijo} Ida y vuelta superaría las jornadas máximas en: ${avisos.map(a => a.modalidad).join(', ')} — se generó solo ida.`,
      });
    } else if (resultados.length) {
      setMsg({ tipo: 'ok', texto: `${textoOk}${sufijo}` });
    } else {
      setMsg({ tipo: 'error', texto: 'Ninguna modalidad tiene participantes suficientes.' });
    }
  };

  const calcularTodo = async () => {
    setGenerando(true); setMsg(null);
    try {
      const resultados = await previsualizarCalendario(temporada);
      setBorradores(prev => ({
        ...prev,
        ...Object.fromEntries(resultados.map(r => [r.modalidad, r])),
      }));
      mostrarResultado(resultados, { esPrevisualizacion: true });
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.message || 'Error al calcular el calendario.' });
    } finally {
      setGenerando(false);
    }
  };

  const calcularModalidad = async (mod) => {
    setMsg(null);
    try {
      const [resultado] = await previsualizarCalendario(temporada, mod);
      setBorradores(prev => ({ ...prev, [mod]: resultado }));
      mostrarResultado([resultado], { esPrevisualizacion: true });
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.message || 'Error al calcular el calendario.' });
    }
  };

  const descartarBorrador = (mod) => setBorradores(prev => {
    const { [mod]: _quitado, ...resto } = prev;
    return resto;
  });

  const guardarBorrador = async (mod) => {
    setGuardandoMod(mod); setMsg(null);
    try {
      const [resultado] = await generarCalendario(temporada, mod);
      descartarBorrador(mod);
      mostrarResultado([resultado]);
      await cargarModalidades();
      onRefresh();
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.message || 'Error al guardar el calendario.' });
    } finally {
      setGuardandoMod(null);
    }
  };

  const borrarOficial = async (mod) => {
    if (!window.confirm(`¿Borrar el calendario oficial de ${mod}? Esta acción no se puede deshacer.`)) return;
    setMsg(null);
    try {
      await borrarCalendario(temporada, mod);
      onRefresh();
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.message || 'Error al borrar el calendario.' });
    }
  };

  const toggleIdaVuelta = async (modalidadId, valor) => {
    try {
      await actualizarIdaVuelta(temporada, modalidadId, valor);
      cargarModalidades();
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.message || 'Error al cambiar ida y vuelta.' });
    }
  };

  const agruparPorJornada = (lista) => {
    const mapa = {};
    lista.forEach(j => {
      if (!mapa[j.jornada]) mapa[j.jornada] = [];
      mapa[j.jornada].push(j);
    });
    return mapa;
  };

  return (
    <div>
      {!soloLectura && (
        <div className="rr-toolbar">
          <button className="btn btn-primary" onClick={calcularTodo} disabled={generando}>
            {generando ? 'Calculando...' : '⟳ Calcular Round Robin (todas las modalidades)'}
          </button>
        </div>
      )}

      {msg && <p className={msg.tipo === 'ok' ? 'success-msg' : msg.tipo === 'aviso' ? 'error-msg' : 'error-msg'}>{msg.texto}</p>}

      {MODALIDADES.map(mod => {
        const info = modalidades.find(m => m.modalidad === mod);
        const borrador = borradores[mod];
        const lista = jornadasPorModalidad(mod);
        if (!info || info.participantes < 1) return null;

        // En borrador se enseña lo calculado (sin guardar); si no, lo oficial.
        const porJornada = borrador
          ? agruparPorJornada(borrador.calendario.map(p => ({
            ...p,
            local_nombre: nombrePorParticipante[p.local] ?? `#${p.local}`,
            visitante_nombre: nombrePorParticipante[p.visitante] ?? `#${p.visitante}`,
          })))
          : agruparPorJornada(lista);

        return (
          <div key={mod} className="modalidad-block">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.5rem' }}>
              <h3>{mod} <span style={{ fontSize: '.78rem', color: '#888', fontWeight: 400 }}>({info.participantes} participantes)</span></h3>
              {!soloLectura && (
                <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label className="rr-toggle">
                    <input
                      type="checkbox"
                      checked={info.ida_y_vuelta}
                      onChange={e => toggleIdaVuelta(info.modalidad_id, e.target.checked)}
                    />
                    Ida y vuelta
                  </label>
                  {info.ida_y_vuelta && !info.cabe_ida_y_vuelta && (
                    <span className="error-msg" style={{ fontSize: '.75rem', margin: 0 }}>
                      ⚠ no cabe en {info.max_jornadas} jornadas: se generará solo ida
                    </span>
                  )}
                  {borrador ? (
                    <>
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: '.78rem' }}
                        onClick={() => guardarBorrador(mod)}
                        disabled={guardandoMod === mod}
                      >
                        {guardandoMod === mod ? 'Guardando...' : '💾 Guardar como oficial'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '.78rem' }}
                        onClick={() => descartarBorrador(mod)}
                        disabled={guardandoMod === mod}
                      >
                        ✕ Descartar
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '.78rem' }}
                        onClick={() => calcularModalidad(mod)}
                      >
                        {lista.length ? 'Recalcular' : 'Calcular'}
                      </button>
                      {lista.length > 0 && (
                        <button
                          className="btn btn-danger"
                          style={{ fontSize: '.78rem' }}
                          onClick={() => borrarOficial(mod)}
                        >
                          🗑 Borrar oficial
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            {borrador && (
              <p className="error-msg" style={{ fontSize: '.78rem', margin: '.3rem 0' }}>
                ⚠ Borrador sin guardar — no sustituye al calendario oficial hasta que pulses "Guardar como oficial".
              </p>
            )}
            {Object.entries(porJornada).map(([j, partidos]) => (
              <div key={j} className="jornada-block">
                <h4>Jornada {j}</h4>
                {partidos.map((p, i) => (
                  <div key={p.id ?? i} className="partido-row">
                    <span className="equipo-label">{p.local_nombre}</span>
                    <span className="vs">vs</span>
                    <span className="equipo-label">{p.visitante_nombre}</span>
                    <span className={`tipo-badge${p.tipo === 'VUELTA' ? ' vuelta' : ''}`}>{p.tipo}</span>
                  </div>
                ))}
              </div>
            ))}
            {!borrador && !lista.length && <p className="rr-empty">Sin jornadas generadas todavía.</p>}
          </div>
        );
      })}

      {!jornadas.length && !Object.keys(borradores).length && <p className="rr-empty">No hay jornadas generadas todavía.</p>}
    </div>
  );
}
